// layouts/dashboard/index.js

// @mui
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";

// secciones
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrderOverview";
import TopLabelsBarList from "./components/TopLabelsBarList";


// data hook
import useCertDashboardData from "./CertDashboard";

// auth & api
import { useAuth } from "layouts/auth/AuthContext";
import api from "services/api";
import { useNavigate } from "react-router-dom";

// Desempaqueta listas que pueden venir en data/page/results/etc.
const unwrapList = (data) =>
  Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.page)
    ? data.page
    : Array.isArray(data?.results)
    ? data.results
    : [];


/* ===== util fechas ===== */
const ymd = (d) => {
  const x = typeof d === "string" ? new Date(d) : d;
  const Y = x.getFullYear();
  const M = String(x.getMonth() + 1).padStart(2, "0");
  const D = String(x.getDate()).padStart(2, "0");
  return `${Y}-${M}-${D}`;
};
const today = () => ymd(new Date());
const addMonths = (d, m) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + m);
  return ymd(x);
};



export default function Dashboard() {
  const {
    user,
    baseUser,
    isImpersonating,
  } = useAuth();

  const isAdmin =
    baseUser?.is_admin === true ||
    baseUser?.is_admin === 1 ||
    String(baseUser?.is_admin) === "1";

  const actingAsAdmin =
    isAdmin && !isImpersonating;
  const navigate = useNavigate();

  // filtros
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");

  const assignedGroupIds = useMemo(() => {
    if (Array.isArray(user?.groups)) {
      return [
        ...new Set(
          user.groups
            .map((g) =>
              typeof g === "object" && g !== null
                ? g.id ?? g._id
                : g
            )
            .filter(
              (id) =>
                id !== null &&
                id !== undefined &&
                id !== ""
            )
            .map(String)
        ),
      ];
    }

    // compatibilidad temporal con usuarios antiguos
    if (user?.group_id !== null && user?.group_id !== undefined) {
      return [String(user.group_id)];
    }

    return [];
  }, [user?.groups, user?.group_id]);

  const dashboardGroupIds = useMemo(() => {
    // Admin normal:
    // sin selección = todos
    // con selección = solo ese grupo
    if (actingAsAdmin) {
      return groupId ? [String(groupId)] : [];
    }

    // Usuario normal o admin impersonando:
    // exclusivamente sus grupos asignados
    return assignedGroupIds;
    }, [
    actingAsAdmin,
    groupId,
    assignedGroupIds,
  ]);
  const [from, setFrom] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
  });
  const [to, setTo] = useState(() => {
    const d = new Date();
    const Y = d.getFullYear(); const M = String(d.getMonth()+1).padStart(2,'0'); const D = String(d.getDate()).padStart(2,'0');
    return `${Y}-${M}-${D}`;
  });

  const handleOpenCertFromTop = useCallback((cert) => {
    if (!cert) return;
    const id = cert.id ?? cert._id;
    if (!id) return;
    navigate(`/certificaciones/${id}/editar`);
  }, [navigate]);
  
  const handleOpenFromStreams = useCallback((item) => {
    const id = item?.id ?? item?._id ?? item?.cert_id ?? item?.certId;
    if (!id) return;
    navigate(`/certificaciones/${id}/editar`);
  }, [navigate]);
  

  // cargar grupos para admins
useEffect(() => {
  if (!actingAsAdmin) return;
  let cancel = false;

  (async () => {
    try {
      // 1) intenta /group
      let list = [];
      try {
        const r1 = await api.get("/group");
        list = unwrapList(r1.data);
      } catch (_) {}

      // 2) fallback /groups
      if (!list.length) {
        try {
          const r2 = await api.get("/groups");
          list = unwrapList(r2.data);
        } catch (_) {}
      }

      if (!cancel) {
        setGroups(
          (list || []).map((g) => ({
            id: String(g.id ?? g._id ?? g.value ?? g.key ?? ""),
            name: g.name ?? g.nombre ?? g.title ?? `#${g.id ?? g._id ?? ""}`,
          }))
        );
      }
    } catch (e) {
      // opcional: muestra algo si quieres
      console.error("Error cargando grupos:", e?.response?.data ?? e?.message ?? e);
    }
  })();

  return () => { cancel = true; };
}, [actingAsAdmin]);

// 👇 AGREGA ESTE NUEVO useEffect PARA EL MES ACTUAL
useEffect(() => {
  // solo al montar
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  const firstDay = `${year}-${month}-01`;
  const todayStr = `${year}-${month}-${day}`;

  setFrom(firstDay);
  setTo(todayStr);
}, []); // <- se ejecuta una sola vez al cargar


  // datos
 const {
   loading, err, kpis, barChart, lineChart, latest,
   barItems,            // para los badges de la barra
   countThisMonth,      // para el subtítulo “X este mes”
   topStreams           // para el panel de “Top por Streams”
 } = useCertDashboardData({
      groupIds: dashboardGroupIds,
      from,
      to,
      allowAll: actingAsAdmin,
    });

  // Ref de la zona a exportar
  const pdfRef = useRef(null);

  const kpisRef = useRef(null);
  const barRef = useRef(null);
  const lineRef = useRef(null);
  const topLabelsRef = useRef(null);
  const tablesRef = useRef(null);



  // exportar PDF (html2canvas + jsPDF: instalar con npm i html2canvas jspdf)
const exportPDF = async () => {
  try {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);

    // helper para capturar un nodo a canvas
    const snap = async (node) => {
      if (!node) return null;
      return await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: node.scrollWidth,
        windowHeight: node.scrollHeight,
      });
    };

    // Capturas (si faltara alguno, seguimos con el otro)
    const canvases = [];
    const topC = await snap(pdfRef?.current);
    if (topC) canvases.push(topC);
    const bottomC = await snap(tablesRef?.current);
    if (bottomC) canvases.push(bottomC);
    if (!canvases.length) throw new Error("No se pudo capturar el contenido del dashboard");

    // Coser en un solo canvas alto
    const stitchedW = Math.max(...canvases.map((c) => c.width));
    const stitchedH = canvases.reduce((s, c) => s + c.height, 0);
    const stitched = document.createElement("canvas");
    stitched.width = stitchedW;
    stitched.height = stitchedH;
    const ctx = stitched.getContext("2d");

    let y = 0;
    for (const c of canvases) {
      const x = Math.round((stitchedW - c.width) / 2);
      ctx.drawImage(c, x, y);
      y += c.height;
    }

    // Datos para el encabezado
    const groupName = groupId
      ? (groups.find((g) => String(g.id) === String(groupId))?.name || `Grupo #${groupId}`)
      : "Todos";
    const dateLabel =
      from || to ? `${from || "inicio"} — ${to || "hoy"}` : "Todos";
    const now = new Date();
    const genLabel = now.toLocaleString();

    // PDF
    const margin = 36;    // márgenes externos
    const headerH = 84;   // altura del encabezado
    const footerH = 24;   // altura del footer

    const a4P = { w: 595.28, h: 841.89 };
    const a4L = { w: 841.89, h: 595.28 };

    const fit = ({ w, h }) =>
      Math.min(
        (w - margin * 2) / stitched.width,
        (h - margin * 2 - headerH - footerH) / stitched.height
      );

    const useLandscape = fit(a4L) > fit(a4P);
    const pdf = new jsPDF(useLandscape ? "landscape" : "portrait", "pt", "a4");
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    // Encabezado (título + subtítulo + línea)
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(17); // #111
    pdf.text("Reporte de certificaciones", margin, margin + 18);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(60);
    pdf.text(
      `Rango: ${dateLabel}   ·   Grupo: ${groupName}   ·   Generado: ${genLabel}`,
      margin,
      margin + 36
    );

    // Línea decorativa (verde AMPROFON aprox)
    pdf.setDrawColor(57, 179, 111);
    pdf.setLineWidth(2);
    pdf.line(margin, margin + 44, pageW - margin, margin + 44);

    // Colocar imagen cosida
    const ratio = Math.min(
      (pageW - margin * 2) / stitched.width,
      (pageH - margin * 2 - headerH - footerH) / stitched.height
    );
    const imgW = stitched.width * ratio;
    const imgH = stitched.height * ratio;
    const x = (pageW - imgW) / 2;
    const yImg = margin + headerH - 28; // ajuste fino según alto de título/subtítulo/linha

    const img = stitched.toDataURL("image/png");
    pdf.addImage(img, "PNG", x, yImg, imgW, imgH);

    // Footer
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text(`Página 1 de 1`, pageW - margin, pageH - margin / 2, { align: "right" });

    pdf.save(`dashboard_${today()}.pdf`);
  } catch (e) {
    console.error(e);
    alert(`Error exportando PDF: ${e?.message || e}`);
  }
};

  // presets de rango
  const presets = [
    { k: "12m", label: "Últimos 12 meses", set: () => { setFrom(addMonths(new Date(), -11)); setTo(today()); } },
    { k: "ytd", label: "Este año",          set: () => { const d = new Date(); setFrom(`${d.getFullYear()}-01-01`); setTo(today()); } },
    { k: "30d", label: "Últimos 30 días",   set: () => { const d = new Date(); d.setDate(d.getDate() - 29); setFrom(ymd(d)); setTo(today()); } },
    { k: "mes", label: "Este mes",          set: () => { const d = new Date(); setFrom(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`); setTo(today()); } },
    { k: "all", label: "Todos", set: () => { setFrom(""); setTo(""); } },
  ];

  // TOP 10 (corta aquí para no renderizar miles de filas)
const latestTop10 = useMemo(() => (latest || []).slice(0, 10), [latest]);

  const mini = useMemo(() => ({
    total:    { title: "Solicitudes totales",   count: String(kpis.total ?? 0),    icon: "insert_chart" },
    accepted: { title: "Solicitudes aceptadas", count: String(kpis.accepted ?? 0), icon: "done_all"     },
    pending:  { title: "Solicitudes pendientes",count: String(kpis.pending ?? 0),  icon: "schedule"     },
    rejected: { title: "Solicitudes rechazadas",count: String(kpis.rejected ?? 0), icon: "cancel"       },
  }), [kpis]);

  const ReportsBarChartMemo = useMemo(() => memo(ReportsBarChart), []);
  const ProjectsMemo = useMemo(() => memo(Projects), []);


  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* ====== Filtros ====== */}
      <SoftBox py={2} px={1}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
          {actingAsAdmin && (
            <TextField
              select
              size="small"
              label="Grupo"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            size="small"
            type="date"
            label="Desde"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />

          <TextField
            size="small"
            type="date"
            label="Hasta"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {presets.map((p) => (
              <Chip key={p.k} label={p.label} onClick={p.set} size="small" />
            ))}
          </Stack>

          <SoftBox flexGrow={1} />

          <SoftButton
            variant="gradient"           // o "outlined" / "contained"
            color="dark"
            onClick={exportPDF}
            startIcon={<Icon>picture_as_pdf</Icon>}
          >
            Descargar PDF
          </SoftButton>
        </Stack>
      </SoftBox>


      {/* ====== Contenido exportable ====== */}
      <div ref={pdfRef}>
        {/* KPIs */}
        <SoftBox py={2}>
  <div ref={kpisRef}>
    <Grid container spacing={3}>
      {/* Total → sin filtro especial (solo navega a la lista) */}
      <Grid item xs={12} sm={6} xl={3}>
        <SoftBox
          onClick={() =>
            navigate("/certificaciones", {
              state: { initialSearch: "" }, // sin filtro, muestra todo
            })
          }
          sx={{ cursor: "pointer" }}
        >
          <MiniStatisticsCard
            title={{ text: mini.total.title }}
            count={mini.total.count}
            percentage={{ color: "success", text: "" }}
            icon={{ color: "info", component: mini.total.icon }}
          />
        </SoftBox>
      </Grid>

      {/* Aceptadas */}
      <Grid item xs={12} sm={6} xl={3}>
        <SoftBox
          onClick={() =>
            navigate("/certificaciones", {
              state: { initialSearch: "estatus:aceptado" },
            })
          }
          sx={{ cursor: "pointer" }}
        >
          <MiniStatisticsCard
            title={{ text: mini.accepted.title }}
            count={mini.accepted.count}
            percentage={{ color: "success", text: "" }}initialStatus 
            icon={{ color: "info", component: mini.accepted.icon }}
          />
        </SoftBox>
      </Grid>

      {/* Pendientes */}
      <Grid item xs={12} sm={6} xl={3}>
        <SoftBox
          onClick={() =>
            navigate("/certificaciones", {
              state: { initialSearch: "estatus:pendiente" },
            })
          }
          sx={{ cursor: "pointer" }}
        >
          <MiniStatisticsCard
            title={{ text: mini.pending.title }}
            count={mini.pending.count}
            percentage={{ color: "error", text: "" }}
            icon={{ color: "info", component: mini.pending.icon }}
          />
        </SoftBox>
      </Grid>

      {/* Rechazadas */}
      <Grid item xs={12} sm={6} xl={3}>
        <SoftBox
          onClick={() =>
            navigate("/certificaciones", {
              state: { initialSearch: "estatus:rechazado" },
            })
          }
          sx={{ cursor: "pointer" }}
        >
          <MiniStatisticsCard
            title={{ text: mini.rejected.title }}
            count={mini.rejected.count}
            percentage={{ color: "error", text: "" }}
            icon={{ color: "info", component: mini.rejected.icon }}
          />
        </SoftBox>
      </Grid>
    </Grid>
  </div>
</SoftBox>

        {/* Gráficas */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <div ref={barRef}>
              <ReportsBarChartMemo
                title="Certificaciones por mes"
                description=""
                chart={barChart}
                items={[]}
                showItems={false}
                height="20.25rem"
                color="dark"
              />
            </div>
            </Grid>
            <Grid item xs={12} lg={7}>
              <div ref={lineRef}>
                <GradientLineChart
                  title="Certificación por Estatus"
                  description={
                    <SoftBox display="flex" alignItems="center">
                      <SoftBox fontSize="lg" color="success" mb={0.3} mr={0.5} lineHeight={0}>
                        <Icon className="font-bold">timeline</Icon>
                      </SoftBox>
                      <SoftTypography variant="button" color="text" fontWeight="regular">
                        Evolución por mes
                      </SoftTypography>
                    </SoftBox>
                  }
                  height="20.25rem"
                  chart={lineChart}
                />
              </div>
            </Grid>
          </Grid>
        </SoftBox>

        {/* Top 4 sellos por conteo (en su propia fila) */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mt: 0 }} ref={topLabelsRef}>
                <SoftBox p={2}>
                  <SoftTypography variant="subtitle2" color="text" sx={{ mb: 1 }}>
                    Top 3 sellos por conteo (período aplicado)
                  </SoftTypography>

                  {Array.isArray(barItems) && barItems.length > 0 ? (
                    <TopLabelsBarList items={(barItems || []).slice(0, 3)} />
                  ) : (
                    <SoftBox py={2} textAlign="center">
                      <SoftTypography variant="button" color="text">
                        Sin datos de sellos para el período seleccionado
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
      </div>

    <div ref={tablesRef}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={8}>
          {/* muestra TODO o limita tú explícitamente */}
          <ProjectsMemo
  latest={latestTop10}
  countThisMonth={countThisMonth}
  onItemClick={handleOpenCertFromTop}
/>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <OrdersOverview top={topStreams} onItemClick={handleOpenFromStreams} />
        </Grid>
      </Grid>
    </div>

      {err && (
        <SoftBox mt={2}>
          <SoftTypography color="error">{String(err)}</SoftTypography>
        </SoftBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
